import React from "react";
import { Box, Grid, Card, CardContent, Typography, Chip } from "@mui/material";
import { ChatSessionDto } from "@/apis/types/SuperChatLogTypes";

interface UserInfoTabProps {
  session: ChatSessionDto;
}

/**
 * 사용자 정보 탭 컴포넌트
 *
 * @description 사용자의 기본 정보와 추가 정보를 표시하는 탭
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const UserInfoTab: React.FC<UserInfoTabProps> = ({ session }) => {
  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* 사용자 기본 정보 카드 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                사용자 정보
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    이름:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {session.user?.userName ||
                      session.sessionUserId ||
                      "게스트"}
                  </Typography>
                </Box>

                {session.user?.userEmail && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      이메일:
                    </Typography>
                    <Typography variant="body2">
                      {session.user.userEmail}
                    </Typography>
                  </Box>
                )}

                {session.user?.userPhone && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      전화번호:
                    </Typography>
                    <Typography variant="body2">
                      {session.user.userPhone}
                    </Typography>
                  </Box>
                )}

                {session.user?.userCompany && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      회사/도장:
                    </Typography>
                    <Typography variant="body2">
                      {session.user.userCompany}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    사용자 타입:
                  </Typography>
                  <Chip
                    label={session.sessionUserType}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 추가 정보 카드 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                추가 정보
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    연락 선호도:
                  </Typography>
                  <Typography variant="body2">
                    {session.user?.contactPreference || "미설정"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    마케팅 동의:
                  </Typography>
                  <Chip
                    label={
                      session.user?.marketingConsent === "Y" ? "동의" : "거부"
                    }
                    size="small"
                    color={
                      session.user?.marketingConsent === "Y"
                        ? "success"
                        : "default"
                    }
                  />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    사업 규모:
                  </Typography>
                  <Typography variant="body2">
                    {session.user?.businessScale || "미설정"}
                  </Typography>
                </Box>

                {session.user?.userType && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      고객 유형:
                    </Typography>
                    <Chip
                      label={session.user.userType}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                )}

                {session.sourceChannel && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      유입 채널:
                    </Typography>
                    <Typography variant="body2">
                      {session.sourceChannel}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

